(() => {
  if (window.iaMobileViewerInstalled) return;
  window.iaMobileViewerInstalled = true;
  const mobile = matchMedia('(max-width: 749px)');
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const labels = JSON.parse(document.getElementById('ia-mobile-viewer-labels')?.textContent || '{}');
  const css = document.createElement('style');
  css.textContent = `
    @media (max-width: 749px) {
      .ia-native-opener, .ia-native-zoom, .product__modal-opener, .product__media-toggle {
        -webkit-tap-highlight-color: transparent;
      }
      modal-opener button[data-ia-viewer-pointer],
      modal-opener button[data-ia-viewer-pointer]::after { outline: none !important; box-shadow: none !important; }
    }
    dialog.ia-mobile-viewer {
      position: fixed; inset: 0; margin: 0; padding: 0; border: 0;
      width: 100%; max-width: none; height: 100%; height: 100dvh; max-height: none;
      overflow: hidden; background: #121b1f; color: #fff; color-scheme: dark; outline: none;
      font-family: var(--font-body-family, sans-serif); overscroll-behavior: none;
    }
    .ia-mobile-viewer::backdrop { background: transparent; }
    .ia-mobile-viewer .ia-viewer-stage {
      position: absolute; inset: 0; display: flex; align-items: stretch; justify-content: flex-start; gap: 16px;
      touch-action: pan-x; overflow-x: auto; overflow-y: hidden; user-select: none; -webkit-user-select: none;
      scroll-snap-type: x mandatory; scroll-behavior: smooth; overscroll-behavior-x: contain;
      scrollbar-width: none; -webkit-overflow-scrolling: touch;
    }
    .ia-mobile-viewer .ia-viewer-stage::-webkit-scrollbar { display: none; }
    .ia-mobile-viewer .ia-viewer-slide {
      position: relative; flex: 0 0 100%; width: 100%; min-width: 0; height: 100%;
      display: flex; align-items: center; justify-content: center; scroll-snap-align: start; scroll-snap-stop: always;
    }
    .ia-mobile-viewer .ia-viewer-slide[hidden] { display: none; }
    .ia-mobile-viewer .ia-viewer-image {
      display: block; flex: none; width: auto; height: auto; max-width: 100%; max-height: 100%;
      object-fit: contain; transform-origin: center;
      user-select: none; -webkit-user-select: none; -webkit-touch-callout: none; pointer-events: none;
    }
    .ia-mobile-viewer .ia-viewer-toolbar {
      position: absolute; top: 0; left: 0; right: 0; display: flex; align-items: center;
      justify-content: space-between; gap: 12px; padding: calc(8px + env(safe-area-inset-top)) 12px 8px;
      background: linear-gradient(#121b1fbb, #121b1f00); pointer-events: none;
    }
    .ia-mobile-viewer .ia-viewer-controls { display: flex; align-items: center; gap: 4px; }
    .ia-mobile-viewer button {
      display: inline-flex; align-items: center; justify-content: center; flex: none;
      width: 44px; height: 44px; margin: 0; padding: 10px; border: 0; border-radius: 50%;
      background: transparent; color: #fff; cursor: pointer; pointer-events: auto;
      -webkit-tap-highlight-color: transparent; touch-action: manipulation;
    }
    .ia-mobile-viewer button:disabled { opacity: .3; }
    .ia-mobile-viewer button[hidden] { display: none; }
    .ia-mobile-viewer button:focus-visible { outline: 2px solid #fff; outline-offset: -4px; }
    .ia-mobile-viewer svg { width: 24px; height: 24px; fill: none; stroke: currentColor; stroke-width: 1.6; }
    .ia-mobile-viewer .ia-viewer-close svg { width: 24px; height: 24px; stroke-width: 1.3; }
    .ia-mobile-viewer .ia-viewer-counter { font-size: 15px; line-height: 1.4; font-weight: 400; }
    .ia-mobile-viewer .ia-viewer-caption {
      position: absolute; bottom: 0; left: 0; right: 0; margin: 0;
      padding: 28px 20px calc(18px + env(safe-area-inset-bottom));
      background: linear-gradient(#121b1f00, #121b1fcc); color: #fff;
      font-family: var(--font-body-family, sans-serif); font-size: 15px; line-height: 1.5;
      text-align: center; pointer-events: none;
    }
  `;
  document.head.append(css);
  let dialog, stage, image, counter, caption, previous, next, toolbar;
  let slides = [];
  let items = [], index = 0, opener, scrollY = 0, bodyStyle, htmlStyle, historyEntry = false;
  let scale = 1, x = 0, y = 0, gesture = null, ignoreTapUntil = 0, lastTap = null;
  let drawFrame = 0, gestureRect = null;
  const pointers = new Map();
  const clamp = (n, a, b) => Math.min(b, Math.max(a, n));
  const isOpen = () => !!dialog?.open;
  const bounds = () => ({
    x: Math.max(0, (image.offsetWidth * scale - stage.clientWidth) / 2),
    y: Math.max(0, (image.offsetHeight * scale - stage.clientHeight) / 2)
  });
  function constrain() {
    const b = bounds();
    x = clamp(x, -b.x, b.x);
    y = clamp(y, -b.y, b.y);
  }
  function draw() {
    if (!image) return;
    image.style.transform = x === 0 && y === 0 && scale === 1 ? 'none' : `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
    const zooming = scale > 1.01 || gesture?.mode === 'pinch';
    stage.style.overflowX = zooming ? 'hidden' : 'auto';
    stage.style.touchAction = zooming ? 'none' : 'pan-x';
    const fade = gesture?.mode === 'dismiss' ? clamp(Math.max(0, y) / stage.clientHeight, 0, .65) : 0;
    dialog.style.backgroundColor = `rgba(18,27,31,${1 - fade})`;
    toolbar.style.opacity = caption.style.opacity = String(1 - fade);
  }
  function queueDraw() {
    if (drawFrame) return;
    drawFrame = requestAnimationFrame(() => { drawFrame = 0; if (isOpen()) draw(); });
  }
  function stopAnimation() {
    if (drawFrame) cancelAnimationFrame(drawFrame);
    drawFrame = 0;
  }
  function reset() {
    stopAnimation();
    pointers.clear(); gesture = null; gestureRect = null; lastTap = null; scale = 1; x = y = 0; draw();
  }
  function fullSource(img) {
    const url = new URL(img.currentSrc || img.src, location.href);
    if (/cdn\.shopify\.com$|\.myshopify\.com$|irina-anis\.com$/.test(url.hostname)) {
      url.searchParams.set('width', '2400');
    }
    return url.href;
  }
  function showItem(target) {
    if (target < 0 || target >= items.length) return;
    if (image) image.style.transform = 'none';
    index = target; image = slides[index].querySelector('img');
    scale = 1; x = y = 0; lastTap = null;
    slides.forEach((slide, position) => {
      slide.setAttribute('aria-hidden', String(position !== index));
      if (position === index) slide.setAttribute('data-active', '');
      else slide.removeAttribute('data-active');
      const photo = slide.querySelector('img');
      photo.alt = position === index ? items[position].title : '';
      if (Math.abs(position - index) <= 1) photo.loading = 'eager';
    });
    caption.textContent = items[index].title;
    counter.textContent = `${index + 1} / ${items.length}`;
    previous.disabled = index === 0; next.disabled = index === items.length - 1;
    previous.hidden = next.hidden = items.length < 2;
    draw();
  }
  function slideTo(target) {
    if (target < 0 || target >= items.length || !isOpen()) return;
    reset();
    stage.style.scrollSnapType = '';
    stage.scrollTo({ left: slides[target].offsetLeft, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
  }
  function close(useHistory = true) {
    if (!isOpen()) return;
    reset(); dialog.close();
    document.body.style.cssText = bodyStyle;
    document.documentElement.style.cssText = htmlStyle;
    window.scrollTo(0, scrollY);
    if (opener?.isConnected) opener.focus({ preventScroll: true });
    if (useHistory && historyEntry && history.state?.iaMobileViewer) history.back();
    historyEntry = false;
  }
  function build() {
    dialog = document.createElement('dialog');
    dialog.className = 'ia-mobile-viewer';
    dialog.tabIndex = -1;
    dialog.setAttribute('aria-label', labels.gallery || labels.enlarge || '');
    const icon = path => `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="${path}" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    dialog.innerHTML = `<div class="ia-viewer-stage"></div><div class="ia-viewer-toolbar"><button class="ia-viewer-close" type="button">${icon('M6 6l12 12M18 6 6 18')}</button><div class="ia-viewer-controls"><button class="ia-viewer-previous" type="button">${icon('m14 6-6 6 6 6')}</button><span class="ia-viewer-counter" aria-live="polite"></span><button class="ia-viewer-next" type="button">${icon('m10 6 6 6-6 6')}</button></div></div><p class="ia-viewer-caption"></p>`;
    document.body.append(dialog);
    stage = dialog.querySelector('.ia-viewer-stage');
    toolbar = dialog.querySelector('.ia-viewer-toolbar'); counter = dialog.querySelector('.ia-viewer-counter');
    caption = dialog.querySelector('.ia-viewer-caption'); previous = dialog.querySelector('.ia-viewer-previous');
    next = dialog.querySelector('.ia-viewer-next');
    const closeButton = dialog.querySelector('.ia-viewer-close');
    closeButton.setAttribute('aria-label', labels.close || '');
    previous.setAttribute('aria-label', labels.previous || ''); next.setAttribute('aria-label', labels.next || '');
    closeButton.addEventListener('click', () => close());
    previous.addEventListener('click', () => slideTo(index - 1));
    next.addEventListener('click', () => slideTo(index + 1));
    dialog.addEventListener('cancel', event => { event.preventDefault(); close(); });
    dialog.addEventListener('keydown', event => {
      if (event.key === 'ArrowLeft') { event.preventDefault(); slideTo(index - 1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); slideTo(index + 1); }
    });
    stage.addEventListener('scroll', () => {
      if (!isOpen() || scale > 1.01) return;
      const nearest = clamp(Math.round(stage.scrollLeft / (stage.clientWidth + 16)), 0, items.length - 1);
      if (nearest !== index) showItem(nearest);
    }, { passive: true });
    stage.addEventListener('pointerdown', start);
    stage.addEventListener('pointermove', move);
    stage.addEventListener('pointerup', end);
    stage.addEventListener('pointercancel', event => end(event, true));
    stage.addEventListener('lostpointercapture', event => { if (pointers.has(event.pointerId)) end(event, true); });
    stage.addEventListener('touchstart', event => {
      if (event.touches.length > 1 || scale > 1.01) event.preventDefault();
    }, { passive: false });
    stage.addEventListener('touchmove', event => {
      if (event.touches.length > 1 || scale > 1.01 || gesture?.mode === 'dismiss') event.preventDefault();
    }, { passive: false });
    stage.addEventListener('click', event => { if (Date.now() < ignoreTapUntil) { event.preventDefault(); event.stopPropagation(); } });
  }
  function open(group, selected, source) {
    if (!group.length || isOpen()) return;
    if (!dialog) build();
    items = group; opener = source; scrollY = window.scrollY;
    const selectedIndex = clamp(selected, 0, items.length - 1);
    slides = items.map((item, position) => {
      const slide = document.createElement('div'); slide.className = 'ia-viewer-slide';
      const photo = document.createElement('img'); photo.className = 'ia-viewer-image';
      photo.draggable = false; photo.decoding = 'async';
      photo.loading = Math.abs(position - selectedIndex) <= 1 ? 'eager' : 'lazy';
      photo.src = item.src; photo.alt = '';
      photo.addEventListener('load', () => { if (isOpen() && image === photo) { constrain(); draw(); } });
      slide.append(photo); return slide;
    });
    stage.replaceChildren(...slides);
    bodyStyle = document.body.style.cssText; htmlStyle = document.documentElement.style.cssText;
    try { history.pushState({ ...history.state, iaMobileViewer: true }, ''); historyEntry = true; } catch { historyEntry = false; }
    document.body.style.position = 'fixed'; document.body.style.top = `-${scrollY}px`;
    document.body.style.left = document.body.style.right = '0'; document.body.style.width = '100%';
    document.documentElement.style.overflow = 'hidden';
    dialog.showModal(); showItem(selectedIndex);
    stage.style.scrollSnapType = '';
    stage.scrollTo({ left: slides[selectedIndex].offsetLeft, behavior: 'instant' });
    dialog.focus({ preventScroll: true });
  }
  function point(event) {
    const r = gestureRect || stage.getBoundingClientRect();
    return { x: event.clientX - r.left - r.width / 2, y: event.clientY - r.top - r.height / 2 };
  }
  function pinch() {
    const [a, b] = [...pointers.values()];
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, distance: Math.hypot(a.x - b.x, a.y - b.y) };
  }
  function start(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (!pointers.size) gestureRect = stage.getBoundingClientRect();
    const p = point(event); pointers.set(event.pointerId, p); stage.setPointerCapture(event.pointerId);
    if (pointers.size === 2) {
      constrain(); const center = pinch();
      gesture = { mode: 'pinch', distance: Math.max(1, center.distance), scale, anchorX: (center.x - x) / scale, anchorY: (center.y - y) / scale, multi: true };
      draw();
    } else if (pointers.size === 1) {
      gesture = { mode: scale > 1.01 ? 'pan' : 'pending', start: p, x, y, time: Date.now(), multi: false, pointerType: event.pointerType, scroll: stage.scrollLeft };
    }
  }
  function move(event) {
    if (!pointers.has(event.pointerId) || !gesture) return;
    const p = point(event); pointers.set(event.pointerId, p);
    if (pointers.size >= 2) {
      if (gesture.mode !== 'pinch') return;
      const center = pinch(); scale = clamp(gesture.scale * center.distance / gesture.distance, 1, 5);
      x = center.x - gesture.anchorX * scale; y = center.y - gesture.anchorY * scale;
      constrain(); queueDraw(); return;
    }
    const dx = p.x - gesture.start.x, dy = p.y - gesture.start.y;
    if (gesture.mode === 'pending' && Math.max(Math.abs(dx), Math.abs(dy)) > 10) {
      gesture.mode = Math.abs(dx) > Math.abs(dy) ? 'swipe' : 'dismiss'; lastTap = null;
    }
    if (gesture.mode === 'pan') { x = gesture.x + dx; y = gesture.y + dy; constrain(); }
    if (gesture.mode === 'swipe') {
      if (event.pointerType === 'mouse') {
        stage.style.scrollSnapType = 'none';
        stage.scrollTo({ left: gesture.scroll - dx, behavior: 'instant' });
      }
      return;
    }
    if (gesture.mode === 'dismiss') { x = dx * .2; y = dy > 0 ? dy : dy * .2; }
    queueDraw();
  }
  function end(event, cancelled = false) {
    if (!pointers.has(event.pointerId)) return;
    const p = point(event), g = gesture; pointers.delete(event.pointerId);
    if (pointers.size) {
      const remaining = [...pointers.values()][0];
      if (pointers.size === 1) gesture = { mode: 'pan', start: remaining, x, y, time: Date.now(), multi: true };
      return;
    }
    if (cancelled || !g || g.multi) { gesture = null; lastTap = null; constrain(); draw(); ignoreTapUntil = Date.now() + 350; return; }
    const dx = p.x - g.start.x, dy = p.y - g.start.y, elapsed = Math.max(1, Date.now() - g.time);
    if (g.mode === 'dismiss' && (dy > Math.min(140, stage.clientHeight * .16) || (dy > 35 && dy / elapsed > .6))) { close(); return; }
    if (g.mode === 'swipe') {
      gesture = null;
      if (g.pointerType === 'mouse') slideTo(clamp(Math.round(stage.scrollLeft / (stage.clientWidth + 16)), 0, items.length - 1));
      ignoreTapUntil = Date.now() + 350; return;
    }
    gesture = null;
    if (g.mode === 'pending' && Math.hypot(dx, dy) < 10) {
      const now = Date.now();
      if (lastTap && now - lastTap.time < 300 && Math.hypot(p.x - lastTap.x, p.y - lastTap.y) < 30) {
        scale = 2.5; x = p.x * (1 - scale); y = p.y * (1 - scale); constrain(); lastTap = null;
      } else lastTap = { ...p, time: now };
    } else if (g.mode === 'pan' && Math.hypot(dx, dy) < 10 && scale > 1.01) {
      const now = Date.now();
      if (lastTap && now - lastTap.time < 300) { scale = 1; x = y = 0; lastTap = null; }
      else lastTap = { ...p, time: now };
    } else lastTap = null;
    if (scale <= 1.01) { scale = 1; x = y = 0; } else constrain();
    draw(); ignoreTapUntil = Date.now() + 350;
  }
  document.addEventListener('pointerdown', event => {
    if (!mobile.matches || !(event.target instanceof Element)) return;
    event.target.closest('modal-opener button')?.setAttribute('data-ia-viewer-pointer', '');
  }, true);
  document.addEventListener('keydown', () => {
    document.querySelectorAll('modal-opener button[data-ia-viewer-pointer]').forEach(button => button.removeAttribute('data-ia-viewer-pointer'));
  }, true);
  document.addEventListener('click', event => {
    if (!mobile.matches || event.defaultPrevented || event.button > 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const button = target.closest('modal-opener button');
    if (!button) return;
    const selector = button.closest('modal-opener').getAttribute('data-modal');
    const modal = selector ? document.querySelector(selector) : null;
    if (!modal?.matches('product-modal')) return;
    let photos = [...modal.querySelectorAll('img[data-media-id]')];
    if (button.closest('.ia-artwork-page')) photos = photos.slice(0, 3);
    if (!photos.length) return;
    const title = document.querySelector('.ia-artwork-heading h1, .product__title h1')?.textContent.trim() || document.title;
    const group = photos.map(img => ({ src: fullSource(img), title }));
    const id = button.getAttribute('data-media-id');
    event.preventDefault(); event.stopImmediatePropagation();
    open(group, photos.findIndex(img => img.getAttribute('data-media-id') === id), button);
  }, true);
  window.addEventListener('popstate', () => { if (isOpen()) close(false); });
  window.addEventListener('resize', () => {
    if (!isOpen()) return;
    if (!mobile.matches) close(); else { stopAnimation(); pointers.clear(); gesture = null; constrain(); draw(); }
  });
})();
